import { useEffect, useState } from 'react';
import FERPA from '../../../../components/FERPA/FerpaComponent';
import CourseUsageFilter from './filter';
import { Col, Row } from 'react-bootstrap';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import { Constants } from '../../../../utils/constants';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faScreenUsers } from '@fortawesome/pro-solid-svg-icons';
import TableSkeleton from '../../../../components/SkeletonLoader/TableSkeleton';
import { useGetCourseUsageReportMutation } from '../../../../features/authentication/courseReport';
import { useSelector } from 'react-redux';
import { selectFerpaState } from '../../../../features/authentication/authSlice';
import '../../styles/reports.css';
import Skeleton from 'react-loading-skeleton';
import { DateFormatter } from '../../../../utils/DateFormatter';
import { convertToCSV, downloadCSV, reorderObjectProperties } from '../../../assignments/utils';
import CustomToast from '../../../../components/Toast';
import ProgressButton from '../../../../components/ProgressButton';
import PageSize from '../../../../enums/pageSize';
import usePrintPdfGenerator from '../../../../utils/PrintPdfGenerator';

const CourseUsageReport = () => {
  const ferpaEnabled = useSelector(selectFerpaState);
  const [courseUsageReportAPI] = useGetCourseUsageReportMutation();
  const [classId, setClassId] = useState(null);
  const [courseId, setCourseId] = useState(null);
  const [customCourseId, setCustomCourseId] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const [showReport, setShowReport] = useState(false);
  const [totalSession, setTotalSession] = useState(null);
  const [totalCorrect, setTotalCorrect] = useState(null);
  const [totalIncorrect, setTotalIncorrect] = useState(null);
  const [totalAssigned, setTotalAssigned] = useState(null);
  const [totalUnassigned, setTotalUnassigned] = useState(null);
  const [totalCrAnswered, setTotalCrAnswered] = useState(null);
  const [totalQuestion, setTotalQuestion] = useState(null);
  const [totalText, setTotalText] = useState('');
  const [courseUsageReport, setCourseUsageReport] = useState(null);
  const [className, setClassName] = useState('');
  const [courseName, setCourseName] = useState('');
  const [reportDate, setReportDate] = useState('');
  const [isCsvGenerating, setIsCsvGenerating] = useState(false);
  const { printPDF, isPdfGenerating } = usePrintPdfGenerator();
  
  const threshold = 65;

  useEffect(() => {
    if (showReport) {
      loadReport();
    }else{
      setCourseUsageReport(null);
    }
  }, [showReport]);

  useEffect(() => {
    if (totalCorrect && totalUnassigned && totalAssigned)
      setTotalText(`<span class="fs-1 fw-semibold"> ${totalCorrect}</span><span class="fs-6">/${totalAssigned+totalUnassigned}</span>`);
    
    setTotalQuestion(totalUnassigned+totalAssigned);
  }, [totalCorrect, totalUnassigned, totalAssigned]);

  const loadReport = async () => {
    if (!courseId || courseId == 0 || (courseId == 255 && (!customCourseId))) {
      return;
    }

    const options =
    {
      CourseId: courseId,
      CustomCourseId: customCourseId,
      ClassId: classId,
      StartDate: DateFormatter.toShortDate2(startDate),
      EndDate: DateFormatter.toShortDate2(endDate)
    };

    var report = await courseUsageReportAPI(options);
    if (report.error) {
      setShowReport(false);
      if (report.error?.data?.error) {
        CustomToast(report.error.data.error, 'error');
        return;
      }
      CustomToast('Report generation failed.', 'error');
      return;
    }

    setCourseUsageReport(report.data.reportData);
    if (report.data.reportData) {
      setTotalSession(report.data.reportData.reduce((acc, item) => acc + item.sessions, 0));
      setTotalCorrect(report.data.reportData.reduce((acc, item) => acc + item.correct, 0));
      setTotalIncorrect(report.data.reportData.reduce((acc, item) => acc + item.incorrect, 0));
      setTotalCrAnswered(report.data.reportData.reduce((acc, item) => acc + item.crAnswered, 0));
      setTotalAssigned(report.data.reportData.reduce((acc, item) => acc + item.assigned, 0));
      setTotalUnassigned(report.data.reportData.reduce((acc, item) => acc + item.unAssigned, 0));
      setReportDate(report.data.reportDate);
    }
  };

  const handlePrint = async () => {
    if(isPdfGenerating) return;

    const contentWrapper = document.getElementById('reportWrapper');
    const htmlString = contentWrapper.innerHTML;
    const tempContainer = document.createElement('div');
    tempContainer.innerHTML = htmlString;

    const ferpaContainer = tempContainer.querySelector('#ferpaContainer');
    if (ferpaContainer) {
      ferpaContainer.classList.add('d-none');
    }

    const buttonElements = tempContainer.querySelectorAll('button');
    buttonElements.forEach((svgElement) => {
      svgElement.classList.add('d-none');
    });
    
    await printPDF('courseUsageReport', tempContainer.innerHTML, PageSize.A3, true);
  };

  const exportHandler = async () => {
    setIsCsvGenerating(true);
    
    const timePeriod = `${DateFormatter.toShortDate2(startDate)} to ${DateFormatter.toShortDate2(endDate)}`;
    const reportDetails = {
      Course: courseName,
      Class: className,
      'Time Period': timePeriod,
      'Report Date': DateFormatter.toLongDateTime(reportDate, true)
    };

    const reportData = [];

    courseUsageReport.forEach((report) => {
      reportData.push({
        'Last Name': ferpaEnabled ? report.lName : 'Lastname hidden',
        'First Name': ferpaEnabled ? report.fName : 'Firstname hidden',
        Sessions: report.sessions,
        Correct: report.correct,
        Incorrect: report.incorrect,
        'CR Answered': report.crAnswered,
        Assigned: report.assigned,
        Unassigned: report.unAssigned,
      });      
    });

    reportData.push({
      'Last Name': 'TOTALS',
      'First Name': '',
      Sessions: totalSession,
      Correct: totalCorrect,
      Incorrect: totalIncorrect,
      'CR Answered': totalCrAnswered,
      Assigned: totalAssigned,
      Unassigned: totalUnassigned,
    });    
    
    const newData = {
      ReportOptions : reportDetails,
      ',,,Question Totals': reportData
    };

    const newDataWithReportOptionsFirst = reorderObjectProperties(newData, 'ReportOptions');
    const csvData = convertToCSV(newDataWithReportOptionsFirst, 'Incorrect totals include \'correct on retry\'');
    downloadCSV(csvData, 'Course Usage Report' );
    setIsCsvGenerating(false);
  };


  return (
    <>
      <div className="white-card">
        {!showReport && <CourseUsageFilter
          setShowReport={setShowReport}
          selectedClassId={classId}
          setSelectedClassId={setClassId}
          selectedCourseId={courseId}
          setSelectedCourseId={setCourseId}
          selectedCustomCourseId={customCourseId}
          setSelectedCustomCourseId={setCustomCourseId}
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          setClassName={setClassName}
          setCourseName={setCourseName}
        />}

        {showReport &&
          <div id="reportWrapper">
            <Row className="align-items-center mb-3">
              <div className="col-md-4">
                <h2 className="primary-title mb-md-0">
                  <span className='fw-medium'>Course Usage Report</span>
                </h2>
              </div>
              <div className="col-md-8 d-flex gap-3 justify-content-end align-items-center">
                <div className="col-xl-1 text-xl-end ps-xl-0 mt-3 mt-xl-0 mr-2" id="ferpaContainer">
                  <FERPA />
                </div>
                <button
                  onClick={() => setShowReport(false)}
                  className="btn btn-width btn-castleWhite report-btn">
                  Back
                </button>
                <button
                  onClick={exportHandler}
                  id="export_button"
                  className="btn btn-width btn-castleOrange report-btn"
                  disabled={!courseUsageReport || isCsvGenerating}>
                  {isCsvGenerating ? 'Generating CSV...' : 'Export'}
                </button>
                <ProgressButton
                  isLoading={isPdfGenerating}
                  onClick={handlePrint}
                  loadingLabel="Generating PDF"
                  label="Print to PDF"
                  className="btn btn-castleRed"
                  disabled={!courseUsageReport}
                />
              </div>
            </Row>
            <div className='report-subHeader-border p-4 rounded-4'>
              {courseUsageReport && <Row>
                <Col className='d-flex gap-1'>
                  <div>Course :</div><div className='fw-semibold'>{courseName}</div>
                </Col>
                <Col className='d-flex gap-1 justify-content-end'><div>Time Period :</div><div className='d-flex'> <span className='fw-semibold gap-2'>{DateFormatter.toShortDate2(startDate)}</span>&nbsp;to&nbsp;<span className='fw-semibold'>{DateFormatter.toShortDate2(endDate)}</span> </div></Col>
              </Row>
              }
              {!courseUsageReport && <Row>
                <Col> <Skeleton className='col-4'></Skeleton></Col>
                <Col> <Skeleton className='col-4'></Skeleton></Col>
              </Row>}
              {courseUsageReport && <Row>
                <Col className='d-flex gap-1'><div>Class:</div><div className='fw-semibold'>{className}</div></Col>
                <Col className='d-flex gap-1 justify-content-end'><div>Report Date:</div><div className='fw-semibold'>{DateFormatter.toLongDateTime2(reportDate)}</div></Col>
              </Row>}
              {!courseUsageReport && <Row>
                <Col> <Skeleton className='col-4'></Skeleton></Col>
                <Col> <Skeleton className='col-4'></Skeleton></Col>
              </Row>}
            </div>
            <Row className='mt-4'>
              <Col>
                <div className='d-flex progressBar-container align-items-center'>
                  <div className='col-lg-3 text-center progressBar-total'>
                    {courseUsageReport &&  <CircularProgressbar
                      className='w-100'
                      value={(totalCorrect/(totalQuestion))*100}
                      text={''}
                      strokeWidth={20}
                      styles={buildStyles(Constants.getProgressBarStyles((totalCorrect/(totalQuestion))*100, threshold))}
                    />
                    }
                    {!courseUsageReport && <Skeleton/>}
                    { courseUsageReport && <div
                      className='progressBar-total-text'
                      dangerouslySetInnerHTML={{ __html: totalText }}
                    /> }
                  </div>
                  <div className='col-lg-9'>
                    <Row>
                      {courseUsageReport &&<Col className='mb-2'><h5>Question Totals</h5></Col>}
                      {!courseUsageReport &&<Col className='mb-2'><Skeleton /></Col>}
                    </Row>
                    <Row>
                      <Col>
                        {courseUsageReport &&
                          <div className="progressBar-card">
                            <div className='bg-white d-flex justify-content-center align-items-center rounded-3 progresssvgDiv'>
                              <FontAwesomeIcon icon={faScreenUsers} color='#82A9E2' />
                            </div>
                            <div className="flex-column m-2">
                              <p>{totalSession}</p>
                              <span>Sessions</span>
                            </div>
                          </div>}
                        {!courseUsageReport && <Skeleton className="progressBar-card"></Skeleton>}
                      </Col>
                      <Col>
                        {courseUsageReport && <div className="progressBar-card">
                          <div className='bg-white d-flex justify-content-center align-items-center rounded-3 progresssvgDiv'>
                            <CircularProgressbar
                              value={(totalAssigned/totalQuestion)*100}
                              strokeWidth={15}
                              styles={buildStyles(Constants.getProgressBarStyles((totalAssigned/totalQuestion)*100, threshold))}
                            />
                          </div>
                          <div className="flex-column m-2">
                            <p>{totalAssigned}</p>
                            <span>Assigned</span>
                          </div>
                        </div>
                        }
                        {!courseUsageReport && <Skeleton className="progressBar-card"></Skeleton>}
                      </Col>
                      <Col>
                        {courseUsageReport && <div className="progressBar-card">
                          <div className='bg-white d-flex justify-content-center align-items-center rounded-3 progresssvgDiv'>
                            <CircularProgressbar
                              value={(totalUnassigned/totalQuestion)*100}
                              strokeWidth={18}
                              styles={buildStyles(Constants.getProgressBarInverseStyles((totalUnassigned/totalQuestion)*100, threshold))}
                            />
                          </div>
                          <div className="flex-column m-2">
                            <p>{totalUnassigned}</p>
                            <span>Unassigned</span>
                          </div>
                        </div>
                        }
                        {!courseUsageReport && <Skeleton className="progressBar-card"></Skeleton>}
                      </Col>
                    </Row>
                    <Row>
                      <Col>
                        {courseUsageReport && <div className="progressBar-card">
                          <div className='bg-white d-flex justify-content-center align-items-center rounded-3 progresssvgDiv'>
                            <CircularProgressbar
                              value={(totalCorrect/totalQuestion)*100}
                              strokeWidth={18}
                              styles={buildStyles(Constants.getProgressBarStyles((totalCorrect/totalQuestion)*100, threshold))}
                            />
                          </div>
                          <div className="flex-column m-2">
                            <p>{totalCorrect}</p>
                            <span>Correct</span>
                          </div>
                        </div>}
                        {!courseUsageReport && <Skeleton className="progressBar-card"></Skeleton>}
                      </Col>
                      <Col>
                        {courseUsageReport && <div className="progressBar-card">
                          <div className='bg-white d-flex justify-content-center align-items-center rounded-3 progresssvgDiv'>
                            <CircularProgressbar
                              value={(totalIncorrect/totalQuestion)*100}
                              strokeWidth={18}
                              styles={buildStyles(Constants.getProgressBarInverseStyles((totalIncorrect/totalQuestion)*100, threshold))}
                            />
                          </div>
                          <div className="flex-column m-2">
                            <p>{totalIncorrect}</p>
                            <span>Incorrect</span>
                          </div>
                        </div>}
                        {!courseUsageReport && <Skeleton className="progressBar-card"></Skeleton>}
                      </Col>
                      <Col>
                        {courseUsageReport && <div className="progressBar-card">
                          <div className='bg-white d-flex justify-content-center align-items-center rounded-3 progresssvgDiv'>
                            <CircularProgressbar
                              value={(totalCrAnswered/totalQuestion)*100}
                              strokeWidth={18}
                              styles={buildStyles(Constants.getProgressBarStyles((totalCrAnswered/totalQuestion)*100, threshold))}
                            />
                          </div>
                          <div className="flex-column m-2">
                            <p>{totalCrAnswered}</p>
                            <span>CR Answered</span>
                          </div>
                        </div>}
                        {!courseUsageReport && <Skeleton className="progressBar-card"></Skeleton>}
                      </Col>
                    </Row>
                  </div>
                </div>
              </Col>
            </Row>

            <Row className='mt-4'>
              {courseUsageReport && <Col>
                <div className='d-flex justify-content-between mb-4'>
                  <div className='fw-medium'>
                    Number Of Students <span className="rounded-number mx-1 fw-semibold">{courseUsageReport?courseUsageReport.length:null}</span>
                  </div>
                  <div className='hideForPrint'>
                    Number Of Questions <span className="rounded-number mx-1 fw-semibold">{courseUsageReport?totalQuestion:''}</span>
                  </div>
                </div>
              </Col>}
              {!courseUsageReport &&  <>
                <Col> <Skeleton className='d-flex gap-1'></Skeleton></Col>
                <Col> <Skeleton className='d-flex gap-1 justify-content-end'></Skeleton></Col>
              </>}
            </Row>
            <Row>
              <Col className={'transparent-scroll table-container1'}>
                <table className='table reportTable'>
                  {courseUsageReport ? (
                    <>
                      <thead className='thead-light align-middle table-head thead-fixed'>
                        <tr>
                          <th scope="col" className='text-start rounded-left fw-medium'>
                            Last Name
                          </th>
                          <th scope="col" className='text-start fw-medium'>
                            First Name
                          </th>
                          <th scope="col" className='fw-medium'>
                            Sessions
                          </th>
                          <th scope="col" className='fw-medium'>
                            Correct
                          </th>
                          <th scope="col" className='fw-medium'>
                            Incorrect
                          </th>
                          <th scope="col" className='fw-medium'>
                            CR Answered
                          </th>
                          <th scope="col" className='fw-medium'>
                            Assigned
                          </th>
                          <th scope="col" className='rounded-right fw-medium'>
                            Unassigned
                          </th>
                        </tr>
                      </thead>
                      <tbody className='transparent-scroll'>
                        {courseUsageReport?.map((report) => (
                          <tr key={`record_${report.studentId}`}>
                            <td>{ferpaEnabled ? report.lName : <i>Hidden</i>}</td>
                            <td>{ferpaEnabled ? report.fName : <i>Hidden</i>}</td>
                            <td className='text-center'>{report.sessions}</td>
                            <td className='text-center'>{report.correct}</td>
                            <td className='text-center'>{report.incorrect}</td>
                            <td className='text-center'>{report.crAnswered}</td>
                            <td className='text-center'>{report.assigned}</td>
                            <td className='text-center'>{report.unAssigned}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className='table-footer'>
                        <tr className='bg-lightred'>
                          <td colSpan={2} className='px-4 text-start'><span className='fw-semibold'>TOTALS</span></td>
                          <td className='text-center'>{totalSession}</td>
                          <td className='text-center'>{totalCorrect}</td>
                          <td className='text-center'>{totalIncorrect}</td>
                          <td className='text-center'>{totalCrAnswered}</td>
                          <td className='text-center'>{totalAssigned}</td>
                          <td className='text-center'>{totalUnassigned}</td>
                        </tr>
                      </tfoot>
                    </>
                  ) : (
                    <TableSkeleton
                      colCount={8}
                      rowCount={4} />
                  )}
                </table>
              </Col>
            </Row>
            <Row className='mt-4'>
              {courseUsageReport && <Col>Incorrect totals include &apos;correct on retry&apos;.</Col>}
            </Row>
          </div>
        }
      </div>

      {showReport &&
        <Row className='mt-4'>
          <Col className='d-flex gap-2 justify-content-end'>
            <button
              onClick={exportHandler}
              id="export_button"
              className="btn btn-width btn-castleWhite report-btn"
              disabled={!courseUsageReport || isCsvGenerating}>
              {isCsvGenerating ? 'Generating CSV...' : 'Export'}
            </button>
            <ProgressButton
              isLoading={isPdfGenerating}
              onClick={handlePrint}
              loadingLabel="Generating PDF"
              label="Print to PDF"
              className="btn btn-castleRed"
              disabled={!courseUsageReport}
            />
          </Col>
        </Row>
      }
    </>
  );
};

export default CourseUsageReport;